import { MontantCalculDirective } from '../../../directives/montant-calcul.directive';
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { OperationPlanifiee } from '../../../models/operation-planifiee.model';
import { OperationReelle } from '../../../models/operation-reelle.model';
import { Compte } from '../../../models/compte.model';

export interface AjoutOperationProvisionPayload {
  libelle: string;
  dateOperation: string;
  montant: number;
  moyenPaiement: string | null;
  numeroCheque: string | null;
  compteDestinationId: number | null;
}

export interface PointageConfirmPayload {
  montant: number;
  moyenPaiement: string | null;
  numeroCheque?: string | null;
  compteDestinationId?: number | null;
  continuer: boolean;
}

@Component({
    selector: 'app-popup-pointage-planifiee',
    imports: [CommonModule, ReactiveFormsModule, MontantCalculDirective],
    templateUrl: './popup-pointage-planifiee.component.html',
    styleUrl: './popup-pointage-planifiee.component.scss'
})
export class PopupPointagePlanifieeComponent implements OnChanges {
  @Input() operation: OperationPlanifiee | null = null;
  @Input() montantInitial = 0;
  @Input() libellePeriode = '';
  @Input() editionAutorisee = false;
  @Input() historiqueUniquement = false;
  @Input() operationsReelles: OperationReelle[] = [];
  @Input() totalReelles = 0;
  @Input() operationsNonPointees: OperationReelle[] = [];
  @Input() enregistrement = false;
  @Input() erreur: string | null = null;
  @Input() moyensPaiement: readonly { label: string; value: string }[] = [];
  @Input() comptesDisponibles: Compte[] = [];

  @Output() confirmer = new EventEmitter<PointageConfirmPayload>();
  @Output() annuler = new EventEmitter<void>();
  @Output() editerOperation = new EventEmitter<number>();
  @Output() depointerOperation = new EventEmitter<number>();
  @Output() pointerOperation = new EventEmitter<number>();
  @Output() reporterOperation = new EventEmitter<number>();
  @Output() supprimerOperation = new EventEmitter<number>();
  @Output() ajouterNonPointee = new EventEmitter<AjoutOperationProvisionPayload>();

  ajoutNonPointe = false;

  @ViewChild('montantInput') montantInput?: ElementRef<HTMLInputElement>;

  private readonly fb = inject(FormBuilder);

  readonly form = this.fb.group({
    libelle: [''],
    dateOperation: [''],
    montant: [0 as number, [Validators.required, Validators.min(0.01)]],
    moyenPaiement: [null as string | null],
    numeroCheque: [{ value: '', disabled: true }],
    compteDestinationId: [{ value: null as number | null, disabled: true }],
  });

  constructor() {
    this.form.get('moyenPaiement')?.valueChanges.subscribe((v) => {
      this.updateMoyenControls(v);
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['operation'] || changes['montantInitial']) && this.operation) {
      this.ajoutNonPointe = false;
      this.form.controls.libelle.clearValidators();
      this.form.controls.dateOperation.clearValidators();
      this.form.enable();
      this.form.reset({
        libelle: this.operation.libelle,
        dateOperation: this.dateDuJour(),
        montant: this.montantInitial,
        moyenPaiement: this.operation.moyenPaiement ?? null,
        numeroCheque:
          this.operation.moyenPaiement === 'CHEQUE'
            ? (this.operation.numeroCheque ?? '')
            : '',
        compteDestinationId:
          this.operation.moyenPaiement === 'VIREMENT'
            ? (this.operation.compteDestinationId ?? null)
            : null,
      });
      this.updateMoyenControls(this.operation.moyenPaiement ?? null);
      this.form.markAsPristine();
      setTimeout(() => {
        this.montantInput?.nativeElement.select();
      }, 0);
    }
  }

  private updateMoyenControls(moyen: string | null | undefined): void {
    const chq = this.form.get('numeroCheque');
    const dest = this.form.get('compteDestinationId');
    if (moyen === 'CHEQUE') {
      chq?.setValidators(this.ajoutNonPointe ? [Validators.required, Validators.pattern(/\S/)] : []);
      chq?.enable({ emitEvent: false });
      dest?.disable({ emitEvent: false });
    } else if (moyen === 'VIREMENT') {
      chq?.disable({ emitEvent: false });
      dest?.setValidators(this.ajoutNonPointe ? Validators.required : []);
      dest?.enable({ emitEvent: false });
    } else {
      chq?.disable({ emitEvent: false });
      dest?.disable({ emitEvent: false });
    }
  }

  private dateDuJour(): string {
    const date = new Date();
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  }

  ouvrirAjoutNonPointe(): void {
    if (!this.operation?.provision || this.enregistrement) return;
    this.ajoutNonPointe = true;
    this.form.controls.libelle.setValidators([Validators.required, Validators.pattern(/\S/)]);
    this.form.controls.dateOperation.setValidators(Validators.required);
    this.form.patchValue({ libelle: this.operation.libelle, dateOperation: this.dateDuJour(), montant: null });
    this.updateMoyenControls(this.form.controls.moyenPaiement.value);
    this.form.markAsPristine();
    this.form.markAsUntouched();
  }

  annulerAjoutNonPointe(): void {
    this.ajoutNonPointe = false;
    this.form.controls.libelle.clearValidators();
    this.form.controls.dateOperation.clearValidators();
    this.form.controls.libelle.updateValueAndValidity();
    this.form.controls.dateOperation.updateValueAndValidity();
    this.form.patchValue({ montant: this.montantInitial });
    this.updateMoyenControls(this.form.controls.moyenPaiement.value);
  }

  get totalNonPointe(): number {
    return this.operationsNonPointees.reduce((total, op) => total + Number(op.montant), 0);
  }

  formatterMontant(montant: number): string {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
    }).format(montant);
  }

  submit(continuer = false): void {
    if (this.enregistrement) return;
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    const val = this.form.getRawValue();
    const moyen = val.moyenPaiement ?? null;
    if (this.ajoutNonPointe && this.operation?.provision) {
      this.ajouterNonPointee.emit({
        libelle: val.libelle!.trim(),
        dateOperation: val.dateOperation!,
        montant: Number(val.montant),
        moyenPaiement: moyen,
        numeroCheque: moyen === 'CHEQUE' ? (val.numeroCheque || null) : null,
        compteDestinationId: moyen === 'VIREMENT' ? (val.compteDestinationId ?? null) : null,
      });
      return;
    }
    this.confirmer.emit({
      montant: Number(val.montant),
      moyenPaiement: moyen,
      numeroCheque: moyen === 'CHEQUE' ? (val.numeroCheque || null) : null,
      compteDestinationId: moyen === 'VIREMENT' ? (val.compteDestinationId ?? null) : null,
      continuer,
    });
    if (continuer) {
      this.form.patchValue({ montant: 0 });
      setTimeout(() => {
        this.montantInput?.nativeElement.focus();
        this.montantInput?.nativeElement.select();
      }, 0);
    }
  }
}
